import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FigmaApprovalAdminPanel } from '../components/FigmaApprovalAdminPanel.jsx';
import { AdminClientFaqSection } from '../components/AdminClientFaqSection.jsx';
import { FaqTemplatesPanel } from '../components/FaqTemplatesPanel.jsx';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { MailboxEmailsList, MailboxMeetingsList } from '../components/MailboxPanels.jsx';
import { MailMessageDialog } from '../components/MailClient.jsx';
import { ProjectMediaLibrary } from '../components/ProjectMediaLibrary.jsx';
import { ProjectFileUploader } from '../components/ProjectFileUploader.jsx';
import { SharePointFileBrowser } from '../components/SharePointFileBrowser.jsx';
import { PdfDocumentsPanel } from '../components/PdfDocumentsPanel.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { WorkspaceTabs } from '../components/WorkspaceTabs.jsx';
import { ProjectHero } from '../components/ProjectHero.jsx';
import { ProjectOverviewBoard } from '../components/ProjectOverviewBoard.jsx';
import { ProjectTeamPanel } from '../components/ProjectTeamPanel.jsx';
import { Modal, ModalConfirmFooter } from '../components/Modal.jsx';
import { TaskBoard } from '../components/TaskBoard.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { projectHealth } from '../utils/statusUi.js';
import { summarizeTaskProgress } from '../utils/taskProgress.js';
import { clientLabel } from '../utils/clientLabel.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const PRIMARY_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'files-media', label: 'Files & Media' },
  { id: 'communication', label: 'Communication' },
  { id: 'forms', label: 'Forms' },
  { id: 'contacts', label: 'Contacts' },
];

const OVERFLOW_TABS = [
  { id: 'pdfs', label: 'PDFs' },
  { id: 'tools', label: 'Tools & Links' },
  { id: 'activity', label: 'Activity' },
  { id: 'settings', label: 'Settings' },
];

export function AdminProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [tab, setTab] = useState('overview');
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [media, setMedia] = useState([]);
  const [contacts, setContacts] = useState(null);
  const [emails, setEmails] = useState(null);
  const [openMailId, setOpenMailId] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [status, setStatus] = useState('draft');
  const [siteUrl, setSiteUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [linkRows, setLinkRows] = useState([{ label: '', url: '' }]);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [analyticsUrl, setAnalyticsUrl] = useState('');
  const [seRankingUrl, setSeRankingUrl] = useState('');
  const [hostingUrl, setHostingUrl] = useState('');
  const [analyticsPropertyId, setAnalyticsPropertyId] = useState('');
  const [seRankingProjectId, setSeRankingProjectId] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [activityTitle, setActivityTitle] = useState('');
  const [activityBody, setActivityBody] = useState('');
  const [clientAssignees, setClientAssignees] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showHeroMore, setShowHeroMore] = useState(false);
  const heroMoreRef = useRef(null);
  const [deleting, setDeleting] = useState(false);

  const load = () =>
    Promise.all([
      apiJson(`/api/admin/projects/${id}`),
      apiJson(`/api/admin/tasks?project_id=${id}`).catch(() => []),
      apiJson(`/api/admin/projects/${id}/media`).catch(() => []),
      apiJson(`/api/admin/projects/${id}/contacts`).catch(() => null),
      apiJson(`/api/admin/projects/${id}/emails`).catch(() => null),
      apiJson(`/api/admin/projects/${id}/meetings`).catch(() => null),
      apiJson('/api/admin/tasks/assignees').catch(() => []),
    ])
      .then(([p, t, m, c, em, mt, a]) => {
        setProject(p);
        setTasks(t);
        setMedia(m);
        setContacts(c);
        setEmails(em);
        setMeetings(mt);
        setAssignees(a);
        setStatus(p.status);
        setSiteUrl(p.site_url || '');
        setNotes(p.notes || '');
        setDueDate(p.due_date || '');
        const ql = Array.isArray(p.quick_links) && p.quick_links.length ? p.quick_links : [{ label: '', url: '' }];
        setLinkRows(ql);
        const meta = p.integration_meta || {};
        setFigmaUrl(meta.figma_url || '');
        setAnalyticsUrl(meta.google_analytics_url || '');
        setSeRankingUrl(meta.se_ranking_url || '');
        setHostingUrl(meta.hosting_url || '');
        setAnalyticsPropertyId(meta.google_analytics_property_id || '');
        setSeRankingProjectId(meta.se_ranking_project_id || '');
        if (p.client_id) {
          apiJson(`/api/admin/tasks/client-assignees?client_id=${p.client_id}`)
            .then(setClientAssignees)
            .catch(() => setClientAssignees([]));
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    void load();
  }, [id]);

  const runSync = async (targets) => {
    setSyncMsg('');
    setError('');
    try {
      const res = await apiJson(`/api/admin/projects/${id}/sync`, { method: 'POST', body: { targets } });
      setSyncMsg(JSON.stringify(res.results, null, 2));
      setProject(res.project);
    } catch (e) {
      setError(e.message);
    }
  };

  const pullGhl = async () => {
    setSyncMsg('');
    setError('');
    try {
      const res = await apiJson(`/api/admin/projects/${id}/sync/ghl/pull`, { method: 'POST' });
      setSyncMsg(JSON.stringify(res, null, 2));
      setProject(res.project);
    } catch (e) {
      setError(e.message);
    }
  };

  const pushGhl = async () => {
    setSyncMsg('');
    setError('');
    try {
      const res = await apiJson(`/api/admin/projects/${id}/sync/ghl/push-status`, { method: 'POST' });
      setSyncMsg(JSON.stringify(res, null, 2));
      setProject(res.project);
    } catch (e) {
      setError(e.message);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setFieldErrors(undefined);
    setError('');
    setSaving(true);
    const quick_links = linkRows
      .filter((r) => r.label.trim() && r.url.trim())
      .map((r) => ({ label: r.label.trim(), url: r.url.trim() }));
    const integration_meta = {
      figma_url: figmaUrl.trim() || null,
      google_analytics_url: analyticsUrl.trim() || null,
      google_analytics_property_id: analyticsPropertyId.trim() || null,
      se_ranking_url: seRankingUrl.trim() || null,
      se_ranking_project_id: seRankingProjectId.trim() || null,
      hosting_url: hostingUrl.trim() || null,
    };
    try {
      const p = await apiJson(`/api/admin/projects/${id}`, {
        method: 'PUT',
        body: {
          status,
          site_url: siteUrl || null,
          notes: notes || null,
          due_date: dueDate || null,
          quick_links: quick_links.length ? quick_links : null,
          integration_meta,
        },
      });
      setProject(p);
      setSyncMsg('Saved.');
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSaving(false);
    }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    try {
      await apiJson(`/api/admin/projects/${id}/activity`, {
        method: 'POST',
        body: { title: activityTitle.trim(), body: activityBody.trim() || null },
      });
      setActivityTitle('');
      setActivityBody('');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const onDelete = async () => {
    setError('');
    setDeleting(true);
    try {
      await apiJson(`/api/admin/projects/${id}`, { method: 'DELETE' });
      navigate('/admin/projects');
    } catch (err) {
      setError(err.message);
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!showHeroMore) {
      return undefined;
    }
    const onDoc = (event) => {
      if (!heroMoreRef.current?.contains(event.target)) {
        setShowHeroMore(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showHeroMore]);

  if (loading && !project) return <p className="muted">Loading…</p>;
  if (!project) return <p className="err">{error || 'Not found'}</p>;

  const health = projectHealth({ ...project, status, due_date: dueDate });
  const progressSummary = summarizeTaskProgress(tasks, project.task_columns);
  const meetingItems = meetings?.items || meetings || [];
  const activityItems = project.status_events || [];

  return (
    <div>
      <ProjectHero
        breadcrumb={<Link to="/admin/projects">← Projects</Link>}
        name={project.name}
        subtitle={`${project.type ? String(project.type).replace(/[_-]/g, ' ') : 'project'}${clientLabel(project.client) ? ` • ${clientLabel(project.client)}` : ''}`}
        status={status}
        statusLabel={health.label}
        statusTone={health.tone}
        progress={progressSummary.percent}
        meta={[
          {
            label: 'Client',
            value: clientLabel(project.client) || project.client_id,
            to: `/admin/clients/${project.client_id}`,
          },
          { label: 'Target completion', value: dueDate ? formatDate(dueDate) : 'TBD' },
          { label: 'Current stage', value: progressSummary.current_stage || health.detail },
        ]}
        actions={
          <>
            {siteUrl ? (
              <a className="btn-secondary" href={siteUrl} target="_blank" rel="noreferrer">
                Open website
              </a>
            ) : null}
            <Link className="btn-primary" to={`/admin/clients/${project.client_id}`}>
              Client view
            </Link>
            <div className="hero-more" ref={heroMoreRef}>
              <button type="button" className="btn-secondary" aria-expanded={showHeroMore} onClick={() => setShowHeroMore((o) => !o)}>
                ⋯
              </button>
              {showHeroMore ? (
                <div className="hero-more-menu">
                  <button
                    type="button"
                    onClick={() => {
                      setTab('settings');
                      setShowHeroMore(false);
                    }}
                  >
                    Project settings
                  </button>
                  <button
                    type="button"
                    className="is-danger"
                    data-tour="project-delete"
                    onClick={() => {
                      setShowHeroMore(false);
                      setShowDelete(true);
                    }}
                  >
                    Delete project
                  </button>
                </div>
              ) : null}
            </div>
          </>
        }
      />
      {error ? <p className="err">{error}</p> : null}
      <div data-tour="project-tabs">
        <WorkspaceTabs tabs={PRIMARY_TABS} overflow={OVERFLOW_TABS} active={tab} onChange={setTab} />
      </div>

      {tab === 'overview' ? (
        <>
        <ProjectTeamPanel
          projectId={project.id}
          members={project.members || []}
          staff={assignees}
          locale={user?.ui_locale}
          onChange={load}
        />
        <ProjectOverviewBoard
          project={project}
          tasks={tasks}
          summary={progressSummary}
          activity={activityItems}
          meetings={Array.isArray(meetingItems) ? meetingItems : []}
          notes={notes}
          clientName={clientLabel(project.client)}
          siteUrl={siteUrl}
          dueDate={dueDate}
          onOpenTab={setTab}
        />
        </>
      ) : null}

      {tab === 'settings' ? (
        <form onSubmit={save} className="auth-card flat stack" style={{ marginTop: '1rem' }}>
          <label className="field">
            <span>Status</span>
            <SearchableSelect value={status} onChange={(ev) => setStatus(ev.target.value)}>
              <option value="draft">draft</option>
              <option value="active">active</option>
              <option value="on_hold">on_hold</option>
              <option value="done">done</option>
              <option value="cancelled">cancelled</option>
            </SearchableSelect>
          </label>
          <label className="field">
            <span>Site URL</span>
            <input value={siteUrl} onChange={(ev) => setSiteUrl(ev.target.value)} />
          </label>
          <label className="field">
            <span>Due date</span>
            <input type="date" value={dueDate} onChange={(ev) => setDueDate(ev.target.value)} />
          </label>
          <div className="auth-card flat">
            <h3>Integrations</h3>
            <p className="muted small">GHL opportunity: {project.ghl_opportunity_id || '—'}</p>
            <p className="muted small">Bitwarden collection: {project.bitwarden_collection_id || '—'}</p>
            <div className="row-actions" style={{ marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn-secondary" onClick={() => void runSync(['ghl'])}>
                Sync GHL
              </button>
              <button type="button" className="btn-secondary" onClick={() => void runSync(['bitwarden'])}>
                Sync Bitwarden
              </button>
              <button type="button" className="btn-secondary" onClick={() => void runSync(['teams'])}>
                Sync Teams folder
              </button>
              <button type="button" className="btn-primary" onClick={() => void runSync(['ghl', 'bitwarden', 'teams'])}>
                Sync all
              </button>
              <button type="button" className="btn-secondary" onClick={() => void pullGhl()}>
                Pull from GHL
              </button>
              <button type="button" className="btn-secondary" onClick={() => void pushGhl()}>
                Push status → GHL
              </button>
            </div>
            {syncMsg ? (
              <pre className="sync-pre" style={{ marginTop: '1rem' }}>
                {syncMsg}
              </pre>
            ) : null}
          </div>
          <FieldErrors errors={fieldErrors} />
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      ) : null}

      {tab === 'communication' ? (
        <div style={{ marginTop: '1rem' }} className="stack">
          <form onSubmit={save} className="auth-card flat stack">
            <label className="field">
              <span>Project notes</span>
              <textarea value={notes} onChange={(ev) => setNotes(ev.target.value)} rows={8} />
            </label>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          </form>
          <div className="auth-card flat">
            <h3 className="panel-title">Emails</h3>
            <p className="muted small">From your Outlook inbox{emails?.mailbox ? ` (${emails.mailbox})` : ''}, filtered to this client.</p>
            <MailboxEmailsList data={emails} loading={loading} onOpen={(email) => setOpenMailId(email.id)} />
            <MailMessageDialog
              apiBase="/api/admin/emails"
              messageId={openMailId}
              canReply
              onClose={() => setOpenMailId(null)}
            />
          </div>
          <div className="auth-card flat">
            <h3 className="panel-title">Meetings</h3>
            <p className="muted small">From your Outlook calendar{meetings?.mailbox ? ` (${meetings.mailbox})` : ''}.</p>
            <MailboxMeetingsList data={meetings} loading={loading} />
          </div>
        </div>
      ) : null}

      {tab === 'tasks' ? (
        <TaskBoard
          tasks={tasks}
          onChange={setTasks}
          projectId={Number(id)}
          assignees={assignees}
          clientAssignees={clientAssignees}
          canDelete={(task) => Boolean(caps.company_admin) || Number(task.created_by) === Number(user?.id)}
          onError={setError}
          columns={project.task_columns}
          onColumnsChange={(taskColumns) => setProject((current) => (current ? { ...current, task_columns: taskColumns } : current))}
        />
      ) : null}

      {tab === 'forms' ? (
        <div style={{ marginTop: '1rem' }}>
          <FaqTemplatesPanel clientId={project.client_id} projectId={id} />
          <div className="auth-card flat">
            <AdminClientFaqSection
              clientId={project.client_id}
              projectId={id}
              heading="Project forms"
              description="Forms and questionnaires visible on this project's portal page."
            />
          </div>
        </div>
      ) : null}

      {tab === 'files-media' ? (
        <div style={{ marginTop: '1rem' }} className="stack media-library-page">
          <div className="auth-card flat">
            <div className="media-library-head">
              <div>
                <h3 className="panel-title">Files & Media</h3>
                <p className="muted small">Photos, logos, documents, and deliverables for this project. New uploads stay internal until you share them.</p>
              </div>
            </div>
            {caps.manage_assets && project.client_id ? (
              <ProjectFileUploader
                compact
                endpoint="/api/admin/assets/upload"
                fields={{ client_id: project.client_id, project_id: id }}
                description="or drag files here"
                onUploaded={() => apiJson(`/api/admin/projects/${id}/media`).then(setMedia)}
              />
            ) : null}
            <ProjectMediaLibrary
              items={media}
              projectId={id}
              mode="staff"
              canManage={Boolean(caps.manage_assets)}
              onRefresh={() => apiJson(`/api/admin/projects/${id}/media`).then(setMedia)}
            />
          </div>
          <div className="auth-card flat">
            <h3 className="panel-title">SharePoint</h3>
            <SharePointFileBrowser
              apiBase={`/api/admin/projects/${id}/sharepoint-files`}
              openInBrowserUrl={project.teams_files_web_url}
            />
          </div>
        </div>
      ) : null}

      {tab === 'pdfs' ? (
        <div className="auth-card flat" style={{ marginTop: '1rem' }}>
          <PdfDocumentsPanel projectId={id} clientId={project.client_id} />
        </div>
      ) : null}

      {tab === 'tools' ? (
        <form onSubmit={save} className="auth-card flat stack" style={{ marginTop: '1rem' }} data-tour="project-tools">
          <div>
            <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>Integration links</span>
            <p className="muted small">Shown to clients on the project Tools tab and My Tools page.</p>
            <label className="field">
              <span>Figma</span>
              <input type="url" value={figmaUrl} onChange={(e) => setFigmaUrl(e.target.value)} placeholder="https://figma.com/…" />
            </label>
            <label className="field">
              <span>Google Analytics</span>
              <input type="url" value={analyticsUrl} onChange={(e) => setAnalyticsUrl(e.target.value)} placeholder="https://analytics.google.com/…" />
            </label>
            <label className="field">
              <span>Google Analytics property ID</span>
              <input value={analyticsPropertyId} onChange={(e) => setAnalyticsPropertyId(e.target.value)} placeholder="123456789" />
            </label>
            <label className="field">
              <span>SE Ranking</span>
              <input type="url" value={seRankingUrl} onChange={(e) => setSeRankingUrl(e.target.value)} placeholder="https://…" />
            </label>
            <label className="field">
              <span>SE Ranking project ID</span>
              <input value={seRankingProjectId} onChange={(e) => setSeRankingProjectId(e.target.value)} placeholder="API project id" />
            </label>
            <label className="field">
              <span>Hosting</span>
              <input type="url" value={hostingUrl} onChange={(e) => setHostingUrl(e.target.value)} placeholder="https://…" />
            </label>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>Quick links</span>
            {linkRows.map((row, idx) => (
              <div key={idx} className="inline-two">
                <input
                  placeholder="Label"
                  value={row.label}
                  onChange={(ev) => {
                    const next = [...linkRows];
                    next[idx] = { ...next[idx], label: ev.target.value };
                    setLinkRows(next);
                  }}
                />
                <input
                  placeholder="https://…"
                  value={row.url}
                  onChange={(ev) => {
                    const next = [...linkRows];
                    next[idx] = { ...next[idx], url: ev.target.value };
                    setLinkRows(next);
                  }}
                />
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              style={{ marginTop: '0.5rem' }}
              onClick={() => setLinkRows([...linkRows, { label: '', url: '' }])}
            >
              Add link row
            </button>
          </div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save links'}
          </button>
        </form>
      ) : null}

      {tab === 'tools' && caps.figma ? (
        <FigmaApprovalAdminPanel projectId={id} figmaUrl={figmaUrl} />
      ) : null}

      {tab === 'contacts' ? (
        <div className="auth-card flat" style={{ marginTop: '1rem' }}>
          {contacts?.client ? (
            <>
              <p>
                <strong>{clientLabel(contacts.client)}</strong>
              </p>
              <p className="muted small">{contacts.client.email || '—'}</p>
              <p className="muted small">{contacts.client.phone || '—'}</p>
              <p className="muted small" style={{ whiteSpace: 'pre-wrap' }}>{contacts.client.address || '—'}</p>
              <p className="muted small">
                GHL: {contacts.client.ghl_contact_id || 'not synced'}
              </p>
              <Link to={`/admin/clients/${contacts.client.id}`}>Open client →</Link>
            </>
          ) : (
            <p className="muted">No contact info.</p>
          )}
        </div>
      ) : null}

      {tab === 'activity' ? (
        <div className="auth-card flat" style={{ marginTop: '1rem' }}>
          <form onSubmit={addActivity} className="stack">
            <h3 style={{ margin: 0 }}>Add update</h3>
            <input
              placeholder="Title"
              value={activityTitle}
              onChange={(e) => setActivityTitle(e.target.value)}
              required
            />
            <textarea
              placeholder="Details (optional)"
              value={activityBody}
              onChange={(e) => setActivityBody(e.target.value)}
              rows={2}
            />
            <button type="submit" className="btn-secondary">
              Post to activity feed
            </button>
          </form>
          <ul className="timeline" style={{ marginTop: '1rem' }}>
            {(project.status_events || []).map((ev) => (
              <li key={ev.id}>
                <strong>{ev.title}</strong>
                <span className="muted small"> · {ev.source}</span>
                {ev.body ? <div className="muted small">{ev.body}</div> : null}
                <div className="muted small">{formatDateTime(ev.created_at)}</div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Modal
        open={showDelete}
        title={`Delete “${project.name}”?`}
        description="This permanently removes the project and its tasks, notes, media, and related data."
        onClose={() => {
          if (!deleting) {
            setShowDelete(false);
          }
        }}
        footer={
          <ModalConfirmFooter
            danger
            confirmLabel="Delete"
            confirmBusy={deleting}
            onCancel={() => setShowDelete(false)}
            onConfirm={() => void onDelete()}
          />
        }
      >
        <p className="muted small" style={{ margin: 0 }}>
          This action is permanent.
        </p>
      </Modal>
    </div>
  );
}
